import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Percent, Gift, Zap, TrendingUp, Search } from 'lucide-react';
import { usePromotions } from './hooks/usePromotions';
import { PromotionsTable } from './components/PromotionsTable';
import { PromotionsDashboard } from './components/PromotionsDashboard';
import { Promotion } from './types';

export const PromotionsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    promotions,
    filteredPromotions,
    stats,
    loading,
    error,
    filters,
    setFilters,
    deletePromotion,
    togglePromotionStatus
  } = usePromotions();

  const [activeTab, setActiveTab] = useState<'overview' | 'promotions'>('overview');

  const handleCreatePromotion = () => {
    navigate('/promotions/new');
  };

  const handleDeletePromotion = async (id: number) => {
    try {
      await deletePromotion(id);
    } catch (error) {
      console.error('Error deleting promotion:', error);
    }
  };

  const handleToggleStatus = async (id: number, currentStatus: boolean) => {
    try {
      await togglePromotionStatus(id, !currentStatus);
    } catch (error) {
      console.error('Error toggling promotion status:', error);
    }
  };

  const handleEditPromotion = (promotion: Promotion) => {
    navigate(`/promotions/edit/${promotion.id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Cargando promociones...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Promociones y Descuentos</h1>
          <p className="text-gray-600 mt-1">Gestiona las promociones y ofertas especiales</p>
        </div>
        <div className="flex gap-3">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'overview'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('promotions')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'promotions'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Promociones
            </button>
          </div>
          <Button onClick={handleCreatePromotion} className="bg-macartur-primary hover:bg-macartur-accent1">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Promoción
          </Button>
        </div>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'overview' ? (
        <PromotionsDashboard stats={stats} promotions={promotions} />
      ) : (

        <>
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Buscar promociones..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="pl-10"
                  />
                </div>
                
                <Select
                  value={filters.type}
                  onValueChange={(value) => setFilters({ ...filters, type: value as any })}
                >
                  <SelectTrigger className="w-full md:w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los tipos</SelectItem>
                    <SelectItem value="percentage">Porcentual</SelectItem>
                    <SelectItem value="fixed">Fijo</SelectItem>
                    <SelectItem value="bxgy">Compra X Lleva Y</SelectItem>
                    <SelectItem value="combo">Combo</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select
                  value={filters.isActive?.toString() || 'all'}
                  onValueChange={(value) => setFilters({
                    ...filters,
                    isActive: value === 'all' ? 'all' : value === 'true'
                  })}
                >
                  <SelectTrigger className="w-full md:w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    <SelectItem value="true">Activo</SelectItem>
                    <SelectItem value="false">Inactivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Promotions Table */}
          <Card>
            <CardContent className="pt-6">
              <PromotionsTable
                promotions={filteredPromotions}
                onEdit={handleEditPromotion}
                onDelete={handleDeletePromotion}
                onToggleStatus={handleToggleStatus}
              />
            </CardContent>
          </Card>
        </>
      )}

    </div>
  );
};